import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  splitInvoiceIntoInstallments,
  type InstallmentPlanItem,
} from "../../../../service/fee-invoices"

interface InvoiceItemLike {
  _id: string
  amount: number
  discount?: number
  fine?: number
  dueDate?: string
  month?: string
  feeName?: string
  description?: string
  feeGroup?: { name?: string }
  feeGroupName?: string
}

interface SplitInstallmentsModalProps {
  show: boolean
  onClose: () => void
  invoice: any
  onSuccess: () => void
}

const isTuitionItem = (item: InvoiceItemLike) => {
  const name = item.feeGroup?.name || item.feeGroupName || ""
  return /tuition/i.test(name)
}

const itemLabel = (item: InvoiceItemLike) =>
  item.feeName || item.description || item.feeGroup?.name || item.feeGroupName || "Fee"

const round2 = (n: number) => Math.round(n * 100) / 100

const buildEqualSplit = (
  principal: number,
  count: number,
  firstDueDate?: string
): InstallmentPlanItem[] => {
  const base = round2(principal / count)
  const startDate = firstDueDate ? dayjs(firstDueDate) : dayjs().add(30, "day")

  const rows = Array.from({ length: count }, (_, idx) => ({
    amount: base,
    dueDate: startDate.add(idx * 30, "day").format("YYYY-MM-DD"),
  }))

  // Equal division can leave a paisa/rupee of rounding error — dump it on
  // the last installment so the rows always sum exactly to the principal.
  const roundedSum = round2(base * count)
  rows[rows.length - 1].amount = round2(rows[rows.length - 1].amount + (principal - roundedSum))

  return rows
}

const SplitInstallmentsModal: React.FC<SplitInstallmentsModalProps> = ({
  show,
  onClose,
  invoice,
  onSuccess,
}) => {
  const eligibleItems: InvoiceItemLike[] = (invoice?.invoiceItems || []).filter(
    (item: InvoiceItemLike) => !isTuitionItem(item)
  )

  const [selectedItemId, setSelectedItemId] = useState<string>("")
  const [numInstallments, setNumInstallments] = useState(2)
  const [rows, setRows] = useState<InstallmentPlanItem[]>([])
  const [submitting, setSubmitting] = useState(false)

  const selectedItem = eligibleItems.find(item => item._id === selectedItemId)
  const principal = selectedItem
    ? round2(selectedItem.amount - (selectedItem.discount || 0))
    : 0

  useEffect(() => {
    if (!show) return
    const firstItem = eligibleItems[0]
    setSelectedItemId(firstItem?._id || "")
    setNumInstallments(2)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, invoice?._id])

  useEffect(() => {
    if (!selectedItem) {
      setRows([])
      return
    }
    setRows(
      buildEqualSplit(
        round2(selectedItem.amount - (selectedItem.discount || 0)),
        numInstallments,
        selectedItem.dueDate
      )
    )
  }, [selectedItemId, numInstallments]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!show || !invoice) return null

  const rowsTotal = round2(rows.reduce((sum, row) => sum + Number(row.amount || 0), 0))
  const totalMismatch = Math.abs(rowsTotal - principal) > 0.01
  const hasInvalidRow = rows.some(row => !(Number(row.amount) > 0) || !row.dueDate)

  const updateRow = (index: number, patch: Partial<InstallmentPlanItem>) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const handleSubmit = async () => {
    if (!selectedItem || totalMismatch || hasInvalidRow) return
    try {
      setSubmitting(true)
      await splitInvoiceIntoInstallments(invoice._id, selectedItem._id, rows)
      toast.success("Fee split into installments")
      onSuccess()
      onClose()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to split fee into installments"
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Split Fee Into Installments</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                disabled={submitting}
              />
            </div>
            <div className="modal-body">
              {eligibleItems.length === 0 ? (
                <p className="text-muted mb-0">
                  Monthly/tuition fees already recur every month and cannot be split into
                  installments.
                </p>
              ) : (
                <>
                  {eligibleItems.length > 1 && (
                    <div className="mb-3">
                      <label className="form-label">Fee to split</label>
                      <select
                        className="form-select"
                        value={selectedItemId}
                        onChange={e => setSelectedItemId(e.target.value)}
                      >
                        {eligibleItems.map(item => (
                          <option key={item._id} value={item._id}>
                            {itemLabel(item)} — {round2(item.amount - (item.discount || 0))}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="form-label">Amount to split</label>
                      <input
                        type="text"
                        className="form-control"
                        value={principal}
                        disabled
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Number of installments</label>
                      <select
                        className="form-select"
                        value={numInstallments}
                        onChange={e => setNumInstallments(Number(e.target.value))}
                      >
                        {[2, 3, 4, 5, 6].map(n => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                      <thead>
                        <tr>
                          <th style={{ width: "60px" }}>#</th>
                          <th>Amount</th>
                          <th>Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>
                              <input
                                type="number"
                                min={0}
                                className="form-control"
                                value={row.amount}
                                onChange={e =>
                                  updateRow(idx, { amount: Number(e.target.value) })
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                className="form-control"
                                value={row.dueDate}
                                onChange={e => updateRow(idx, { dueDate: e.target.value })}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className={`d-flex justify-content-between fw-semibold ${
                      totalMismatch ? "text-danger" : "text-success"
                    }`}
                  >
                    <span>Total of installments</span>
                    <span>
                      {rowsTotal} / {principal}
                    </span>
                  </div>
                  {totalMismatch && (
                    <p className="text-danger small mb-0 mt-1">
                      Installments must add up to the full amount before splitting.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              {eligibleItems.length > 0 && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={submitting || totalMismatch || hasInvalidRow}
                >
                  {submitting ? "Splitting..." : "Split Into Installments"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  )
}

export default SplitInstallmentsModal
